
import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Webhook, Loader2, CheckCircle2, AlertCircle, Copy } from "lucide-react";
import { useAppSettings } from "@/hooks/useAppSettings";

const AUTOCOMPLETE_SETTING_KEY = 'enableLocationAutocomplete';

const AdminSettings: React.FC = () => {
  const [enableAutocomplete, setEnableAutocomplete] = useState(true);
  const [linkedinScraperModel, setLinkedinScraperModel] = useState<string>('pratikdani');
  const [showLinkedInSection, setShowLinkedInSection] = useState(false);
  const [autoEnrichmentEnabled, setAutoEnrichmentEnabled] = useState(true);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [isSettingUpWebhook, setIsSettingUpWebhook] = useState(false);
  const [webhookResult, setWebhookResult] = useState<{
    success: boolean;
    webhook_id?: string;
    webhook_url?: string;
    signing_secret?: string;
    message?: string;
    error?: string;
  } | null>(null);
  const [isSettingUpPlans, setIsSettingUpPlans] = useState(false);
  const [plansResult, setPlansResult] = useState<{
    success: boolean;
    plan_id?: string;
    variations?: Array<{ tier: string; variation_id: string }>;
    message?: string;
    error?: string;
  } | null>(null);
  const { toast } = useToast();
  const { invalidate: invalidateAppSettings } = useAppSettings();

  useEffect(() => {
    const saved = localStorage.getItem(AUTOCOMPLETE_SETTING_KEY);
    if (saved !== null) {
      setEnableAutocomplete(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoadingSettings(false);
        return;
      }

      const { data, error } = await supabase
        .from('settings')
        .select('linkedin_scraper_model, show_linkedin_section, auto_enrichment_enabled')
        .eq('user_id', user.id)
        .single();

      if (data) {
        setLinkedinScraperModel(data.linkedin_scraper_model);
        setShowLinkedInSection(data.show_linkedin_section ?? false);
        setAutoEnrichmentEnabled(data.auto_enrichment_enabled ?? true);
      }
      setIsLoadingSettings(false);
    };

    fetchSettings();
  }, []);

  const handleToggleAutocomplete = (enabled: boolean) => {
    setEnableAutocomplete(enabled);
    localStorage.setItem(AUTOCOMPLETE_SETTING_KEY, JSON.stringify(enabled));
    
    toast({
      title: "Setting Updated",
      description: `Location autocomplete ${enabled ? 'enabled' : 'disabled'}. This will reduce Google API costs when disabled.`,
    });
  };

  const handleScraperModelChange = async (model: string) => {
    setLinkedinScraperModel(model);
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('settings')
      .upsert({
        user_id: user.id,
        linkedin_scraper_model: model,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id'
      });

    if (error) {
      toast({
        variant: "destructive",
        title: "Failed to save setting",
        description: error.message,
      });
      return;
    }

    toast({
      title: "Setting Updated",
      description: `LinkedIn scraper changed to ${model === 'pratikdani' ? 'Pratik Dani' : 'Enhanced Scraper'}. Future enrichments will use this model.`,
    });
  };

  const handleLinkedInSectionToggle = async (enabled: boolean) => {
    setShowLinkedInSection(enabled);
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('settings')
      .upsert({
        user_id: user.id,
        show_linkedin_section: enabled,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id'
      });

    if (error) {
      toast({
        variant: "destructive",
        title: "Failed to save setting",
        description: error.message,
      });
      setShowLinkedInSection(!enabled); // Revert on error
      return;
    }

    toast({
      title: "Setting Updated",
      description: `LinkedIn Information section ${enabled ? 'enabled' : 'disabled'} on lead pages.`,
    });
  };

  const handleAutoEnrichmentToggle = async (enabled: boolean) => {
    setAutoEnrichmentEnabled(enabled);
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('settings')
      .upsert({
        user_id: user.id,
        auto_enrichment_enabled: enabled,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id'
      });

    if (error) {
      toast({
        variant: "destructive",
        title: "Failed to save setting",
        description: error.message,
      });
      setAutoEnrichmentEnabled(!enabled);
      return;
    }

    invalidateAppSettings();

    toast({
      title: "Setting Updated",
      description: `Auto enrichment pipeline ${enabled ? 'enabled' : 'disabled'}.`,
    });
  };

  const handleSetupWebhook = async () => {
    setIsSettingUpWebhook(true);
    setWebhookResult(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Not authenticated. Please log in again.');
      }

      const { data, error } = await supabase.functions.invoke('setup-revolut-webhook', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) throw error;

      setWebhookResult(data);
      
      if (data.success) {
        toast({
          title: "Webhook Created Successfully",
          description: "Copy the signing secret below and update REVOLUT_WEBHOOK_SECRET in Supabase secrets.",
        });
      }
    } catch (error: any) {
      console.error('Webhook setup error:', error);
      setWebhookResult({
        success: false,
        error: error.message || 'Failed to set up webhook'
      });
      
      toast({
        variant: "destructive",
        title: "Webhook Setup Failed",
        description: error.message || 'Failed to set up webhook',
      });
    } finally {
      setIsSettingUpWebhook(false);
    }
  };

  const handleSetupPlans = async () => {
    setIsSettingUpPlans(true);
    setPlansResult(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Not authenticated. Please log in again.');
      }

      const { data, error } = await supabase.functions.invoke('setup-revolut-plans', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) throw error;

      setPlansResult(data);
      
      if (data.success) {
        toast({
          title: "Plans Created Successfully",
          description: `Plan ID: ${data.plan_id}. Variation IDs saved to revolut_plan_config.`,
        });
      }
    } catch (error: any) {
      console.error('Plans setup error:', error);
      setPlansResult({
        success: false,
        error: error.message || 'Failed to set up plans'
      });
      
      toast({
        variant: "destructive",
        title: "Plans Setup Failed",
        description: error.message || 'Failed to set up plans',
      });
    } finally {
      setIsSettingUpPlans(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: `${label} copied to clipboard`,
    });
  };

  return (
    <div className="space-y-4">
      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>App Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="autocomplete-toggle">Location Autocomplete</Label>
              <p className="text-sm text-muted-foreground">
                Enable Google Places autocomplete for location inputs. Disabling this reduces API costs.
              </p>
            </div>
            <Switch
              id="autocomplete-toggle"
              checked={enableAutocomplete}
              onCheckedChange={handleToggleAutocomplete}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>Auto Enrichment Pipeline</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="auto-enrichment-toggle">Enable Auto Enrichment</Label>
              <p className="text-sm text-muted-foreground">
                Automatically enrich leads in the background (web search, crawl, deep research). 
                Disabling this stops the automatic pipeline. Manual enrichment and import-triggered enrichment still work.
              </p>
            </div>
            <Switch
              id="auto-enrichment-toggle"
              checked={autoEnrichmentEnabled}
              onCheckedChange={handleAutoEnrichmentToggle}
              disabled={isLoadingSettings}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>LinkedIn Enrichment</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="linkedin-scraper">LinkedIn Scraper Model</Label>
            <Select 
              value={linkedinScraperModel} 
              onValueChange={handleScraperModelChange}
              disabled={isLoadingSettings}
            >
              <SelectTrigger id="linkedin-scraper">
                <SelectValue placeholder="Select scraper model" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pratikdani">
                  <div className="flex flex-col">
                    <span className="font-medium">Pratik Dani Scraper</span>
                    <span className="text-xs text-muted-foreground">Basic company info</span>
                  </div>
                </SelectItem>
                <SelectItem value="enhanced">
                  <div className="flex flex-col">
                    <span className="font-medium">Enhanced Scraper</span>
                    <span className="text-xs text-muted-foreground">Employees, updates, similar companies</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Choose which LinkedIn scraping actor to use. Enhanced scraper provides more detailed information.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>LinkedIn Section Visibility</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="linkedin-section-toggle">Show LinkedIn Information</Label>
              <p className="text-sm text-muted-foreground">
                Display the LinkedIn Information card on lead detail pages. 
                Disable this to hide the section when it's not providing useful data.
              </p>
            </div>
            <Switch
              id="linkedin-section-toggle"
              checked={showLinkedInSection}
              onCheckedChange={handleLinkedInSectionToggle}
              disabled={isLoadingSettings}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Webhook className="h-5 w-5" />
            Revolut Subscription Setup
          </CardTitle>
          <CardDescription>
            Initialize subscription plans and webhooks for Revolut payment processing
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Setup Plans Section */}
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium mb-1">1. Setup Subscription Plans</h4>
              <p className="text-sm text-muted-foreground">
                Creates the subscription plan with Starter, Pro, and Unlimited variations in Revolut and saves variation IDs locally.
              </p>
            </div>

            <Button 
              onClick={handleSetupPlans}
              disabled={isSettingUpPlans}
              className="w-full sm:w-auto"
            >
              {isSettingUpPlans ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating plans...
                </>
              ) : (
                'Setup Subscription Plans'
              )}
            </Button>

            {plansResult && (
              <Alert variant={plansResult.success ? "default" : "destructive"}>
                <div className="flex items-start gap-2">
                  {plansResult.success ? (
                    <CheckCircle2 className="h-4 w-4 mt-0.5" />
                  ) : (
                    <AlertCircle className="h-4 w-4 mt-0.5" />
                  )}
                  <div className="flex-1 space-y-2">
                    <AlertDescription>
                      {plansResult.success ? (
                        <div className="space-y-2">
                          <p className="font-medium">Plans created successfully!</p>
                          {plansResult.plan_id && (
                            <div className="space-y-1">
                              <Label className="text-xs">Plan ID:</Label>
                              <div className="flex items-center gap-2">
                                <code className="flex-1 text-xs bg-muted px-2 py-1 rounded">
                                  {plansResult.plan_id}
                                </code>
                                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(plansResult.plan_id!, 'Plan ID')}>
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          )}
                          {plansResult.variations && plansResult.variations.length > 0 && (
                            <div className="space-y-1">
                              <Label className="text-xs">Variations:</Label>
                              {plansResult.variations.map((v) => (
                                <div key={v.tier} className="flex items-center gap-2 text-xs">
                                  <span className="font-medium capitalize w-20">{v.tier}:</span>
                                  <code className="bg-muted px-2 py-1 rounded flex-1">{v.variation_id}</code>
                                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(v.variation_id, `${v.tier} variation ID`)}>
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <p>{plansResult.error || 'Failed to set up plans'}</p>
                      )}
                    </AlertDescription>
                  </div>
                </div>
              </Alert>
            )}
          </div>

          <hr className="border-border" />

          {/* Setup Webhook Section */}
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium mb-1">2. Setup Webhook</h4>
              <p className="text-sm text-muted-foreground">
                Creates a webhook in your Revolut account (sandbox or production based on REVOLUT_MODE setting).
              </p>
            </div>

            <Button 
              onClick={handleSetupWebhook}
              disabled={isSettingUpWebhook}
              className="w-full sm:w-auto"
            >
              {isSettingUpWebhook ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting up webhook...
                </>
              ) : (
                <>
                  <Webhook className="mr-2 h-4 w-4" />
                  Setup Revolut Webhook
                </>
              )}
            </Button>

            {webhookResult && (
              <Alert variant={webhookResult.success ? "default" : "destructive"}>
                <div className="flex items-start gap-2">
                  {webhookResult.success ? (
                    <CheckCircle2 className="h-4 w-4 mt-0.5" />
                  ) : (
                    <AlertCircle className="h-4 w-4 mt-0.5" />
                  )}
                  <div className="flex-1 space-y-3">
                    <AlertDescription>
                      {webhookResult.success ? (
                        <div className="space-y-3">
                          <p className="font-medium">Webhook created successfully!</p>
                          
                          {webhookResult.webhook_id && (
                            <div className="space-y-1">
                              <Label className="text-xs">Webhook ID:</Label>
                              <div className="flex items-center gap-2">
                                <code className="flex-1 text-xs bg-muted px-2 py-1 rounded">
                                  {webhookResult.webhook_id}
                                </code>
                                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(webhookResult.webhook_id!, 'Webhook ID')}>
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          )}

                          {webhookResult.webhook_url && (
                            <div className="space-y-1">
                              <Label className="text-xs">Webhook URL:</Label>
                              <div className="flex items-center gap-2">
                                <code className="flex-1 text-xs bg-muted px-2 py-1 rounded break-all">
                                  {webhookResult.webhook_url}
                                </code>
                                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(webhookResult.webhook_url!, 'Webhook URL')}>
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          )}

                          {webhookResult.signing_secret && (
                            <div className="space-y-1">
                              <Label className="text-xs">Signing Secret:</Label>
                              <div className="flex items-center gap-2">
                                <code className="flex-1 text-xs bg-muted px-2 py-1 rounded break-all">
                                  {webhookResult.signing_secret}
                                </code>
                                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(webhookResult.signing_secret!, 'Signing Secret')}>
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          )}

                          <Alert variant="default" className="bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800">
                            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                            <AlertDescription className="text-amber-800 dark:text-amber-200">
                              <p className="font-medium mb-2">⚠️ IMPORTANT: Update Supabase Secret</p>
                              <p className="text-sm mb-2">
                                Copy the signing secret above, then go to{' '}
                                <strong>Supabase Dashboard → Settings → Edge Functions → Secrets</strong> and set{' '}
                                <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">REVOLUT_WEBHOOK_SECRET</code> to this value.
                              </p>
                              <p className="text-xs text-muted-foreground">
                                The webhook will not verify incoming requests until this secret is configured.
                              </p>
                            </AlertDescription>
                          </Alert>
                        </div>
                      ) : (
                        <p>{webhookResult.error || 'Failed to set up webhook'}</p>
                      )}
                    </AlertDescription>
                  </div>
                </div>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default AdminSettings;
